import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DatePicker from 'react-multi-date-picker';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext';
import { API_URLS } from '../../config';
import { apiRequest } from '../../utils/apiRequest';
import { useToast } from '../../Components/Toast';
import { groupDatesByMonth, monthlyShareAmount } from '../../utils/tentativeDates';
import { FiRefreshCw, FiEdit, FiCheck, FiX, FiExternalLink, FiFile, FiBell, FiClock, FiInfo, FiSearch, FiPlus } from 'react-icons/fi';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import FilterPanel, { FilterField, filterSelectStyles } from '../../Components/FilterPanel';
import './Finance.css';

const PAYABLE_STATUSES = ['Booked', 'Not Payable', 'Payable', 'Paid', 'Partial Paid'];

// "Invoice Amount (X of Y day(s))" — shared by both the Pay and Partial Pay read-only
// summaries below. Manmonth prorates its monthly rate per day (see
// proratedMonthlyAmount), so the "planned" unit is a day count for both types; Manmonth
// has no billing.days of its own, so fall back to the inspector's attended date count.
const invoiceAmountLabel = (row) => {
  const attended = (row.workOrder?.inspectionDates || []).filter(d => !(row.workOrder?.absentDates || []).includes(d)).length;
  const planned = row.workOrder?.billing?.days || attended;
  return row.daysCovered
    ? `Invoice Amount (${row.daysCovered} of ${planned || '?'} day(s))`
    : 'Invoice Amount';
};

const payableStatusStyle = (s) => {
  if (s === 'Paid')         return { background: '#d1fae5', color: '#065f46' };
  if (s === 'Not Payable')  return { background: '#fee2e2', color: '#991b1b' };
  if (s === 'Partial Paid') return { background: '#fef3c7', color: '#92400e' };
  if (s === 'Payable')      return { background: '#dbeafe', color: '#1e40af' };
  return { background: '#e5e7eb', color: '#374151' }; // Booked (default)
};

// Rate the app implicitly used to turn "Cost" into "Cost INR" — the default invoicing
// rate shown/edited until the user overrides it. Prefers the Currency Rate Master's
// rate as of this invoice's own Invoice Date (rateAsOfInvoiceDate) when available,
// falling back to the cost/costINR ratio otherwise.
const defaultInvoicingRate = (r) => {
  if (r.rateAsOfInvoiceDate != null) return r.rateAsOfInvoiceDate;
  const cost = parseFloat(r.cost) || 0;
  const costINR = parseFloat(r.costINR) || 0;
  return cost > 0 ? costINR / cost : 1;
};

// Client-side counterpart to rateFromHistoryAsOf in backend/utils/currencyRates.js —
// looks up the most recent dated entry on or before dateStr, from a rate history
// fetched once (see rateHistory state below).
const rateFromHistoryAsOf = (history, code, dateStr) => {
  if (!code || code === 'INR') return 1;
  const entries = history[code];
  const asOfTime = new Date(dateStr).getTime();
  if (!entries?.length || isNaN(asOfTime)) return null;
  let best = null;
  for (const e of entries) { if (new Date(e.date).getTime() <= asOfTime) best = e.rate; else break; }
  return best;
};

// One overtime entry's amount — hours × rate. Ported from src/pages/Manday.jsx.
// Overtime exists per-invoice only (invoice.overtime), not on the work order's billing.
const overtimeEntryAmount = (entry) => (parseFloat(entry?.hours) || 0) * (parseFloat(entry?.rate) || 0);

// Sum of a work order's itemized other-cost entries. Ported from src/pages/Manday.jsx.
const otherCostEntriesTotalOf = (otherCostEntries) =>
  (otherCostEntries || []).reduce((s, oc) => s + (parseFloat(oc?.amount) || 0), 0);

// inspectorAgency carries a "(n)" suffix (e.g. "Jane Doe (2)") when the same
// inspector was booked more than once on the enquiry, so Manday.jsx can tell the
// bookings apart internally. That disambiguator isn't meant for display here —
// strip it so this page just shows the inspector's actual name.
const inspectorDisplayName = (agency) => (agency || '').replace(/\s*\(\d+\)$/, '');

export default function InspectionInvoice() {
  const { token } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  // Header filters — company/coordinator/inspector/job number/month, all narrowing
  // the already-fetched `rows` client-side (this page has no server pagination).
  const [companyFilter, setCompanyFilter] = useState('');
  const [coordinatorFilter, setCoordinatorFilter] = useState('');
  const [inspectorFilter, setInspectorFilter] = useState('');
  const [jobNumberFilter, setJobNumberFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [rateUpdating, setRateUpdating] = useState({});
  const [unlocked, setUnlocked] = useState({});   // rowId -> true once "unlock to edit" is clicked
  const [drafts, setDrafts] = useState({});       // rowId -> typed-but-unsaved rate value

  // Notifications — customer payment received, or 45 days since invoice date — ready to mark inspector payable
  const [notifications, setNotifications] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [notifActioning, setNotifActioning] = useState({}); // notifId -> true while its action is in flight

  // "Pay" / "Partial Pay" — check one Payable (or partially-paid-with-remainder) row
  const [selectedId, setSelectedId] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState({ paymentDate: '', inspectorPaymentRate: '', tdsAmount: '' });
  const [payRateTouched, setPayRateTouched] = useState(false); // stops auto-updating the rate from Payment Date once typed manually
  const [paying, setPaying] = useState(false);
  const [showPartialPayModal, setShowPartialPayModal] = useState(false);
  // `amount` here is the gross amount being settled (cash + TDS); cash = amount − tdsAmount.
  const [partialPayForm, setPartialPayForm] = useState({ paymentDate: '', inspectorPaymentRate: '', amount: '', tdsAmount: '' });
  const [partialPayRateTouched, setPartialPayRateTouched] = useState(false);
  const [partialPaying, setPartialPaying] = useState(false);

  // Full dated currency-rate history ({ CODE: [{date, rate}, ...] }, ascending) — fetched
  // once so Inspector Payment Rate can auto-update live as Payment Date changes in the
  // Pay/Partial Pay modals.
  const [rateHistory, setRateHistory] = useState({});
  useEffect(() => {
    (async () => {
      const result = await apiRequest(API_URLS.FINANCE_CURRENCY_RATES, {}, token);
      if (!result?.ok) return;
      const byCode = {};
      (result.data?.rows || []).forEach(r => {
        if (!byCode[r.code]) byCode[r.code] = [];
        byCode[r.code].push({ date: r.date, rate: r.rate });
      });
      Object.values(byCode).forEach(entries => entries.sort((a, b) => new Date(a.date) - new Date(b.date)));
      setRateHistory(byCode);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Payment History popup — looked up live from `rows` (not a snapshot) so it stays
  // in sync if a payment is recorded while it's open.
  const [historyRowId, setHistoryRowId] = useState(null);

  // Work Order Details popup — full read-only view of the work order (location,
  // description, dates, billing breakdown incl. OT/Other Costs), separate from the
  // Pay/Partial Pay popups which stay focused on recording a payment.
  const [workOrderDetailRowId, setWorkOrderDetailRowId] = useState(null);

  // Edit Invoice popup — corrects Invoice Number/Date/Days Covered on an already-
  // uploaded invoice; the Finance-side counterpart to Manday.jsx, locked once a payment
  // is recorded — see payableController.editInvoice.
  const [editRowId, setEditRowId] = useState(null);
  const [editForm, setEditForm] = useState({ invoiceNumber: '', invoiceDate: '', datesCovered: [], tax: 0, otherCostEntries: [], overtime: [] });
  const [editSaving, setEditSaving] = useState(false);
  const editRow = rows.find(r => r._id === editRowId) || null;

  // Dates this invoice can pick from — planned dates, minus Absent, minus any date a
  // sibling invoice already claims. Same rule Manday.jsx's Add Invoice form enforces;
  // the server (payableController.editInvoice) is the source of truth.
  const editAvailableDates = useMemo(() => {
    if (!editRow) return [];
    const wo = editRow.workOrder || {};
    const allDates = Array.isArray(wo.inspectionDates) ? wo.inspectionDates : [];
    const absent = new Set(Array.isArray(wo.absentDates) ? wo.absentDates : []);
    const claimedByOthers = new Set((editRow.siblingInvoices || []).flatMap(s => Array.isArray(s.datesCovered) ? s.datesCovered : []));
    return allDates.filter(d => !absent.has(d) && !claimedByOthers.has(d));
  }, [editRow]);

  // Tax Rate is still work-order-level and shared — editing it moves every sibling
  // invoice's amount too, so it's only allowed while all of them are unpaid. Overtime/
  // Other Costs are per-invoice (see editBreakdown below) and need no separate lock —
  // this invoice's own Edit button already disables once it has a payment.
  const editBillingLocked = useMemo(() => {
    if (!editRow) return true;
    const selfPaid = (parseFloat(editRow.amountPaid) || 0) > 0;
    const siblingPaid = (editRow.siblingInvoices || []).some(s => (parseFloat(s.amountPaid) || 0) > 0);
    return selfPaid || siblingPaid;
  }, [editRow]);

  // Date-by-date breakdown of what THIS invoice is worth — reconciles exactly to what
  // the server (computeInvoiceShare) will compute and save; same formula as
  // invoiceBreakdown in src/pages/Manday.jsx / Manmonth.jsx. For each month this
  // invoice's dates touch, Manmonth divides the monthly rate by that month's
  // user-entered Working Days figure (billing.workingDaysByMonth, set in Manday.jsx's
  // Billing Details) and multiplies by however many of those days THIS invoice covers.
  // A month with no Working Days figure set falls back to however many days were
  // ORIGINALLY assigned that month (the full plan, absences included).
  const editBreakdown = useMemo(() => {
    if (!editRow) return null;
    const billing = editRow.workOrder?.billing || {};
    const isManmonth = editRow.enquiryType === 'MANMONTH';
    const rate = parseFloat((isManmonth ? billing.monthlyRate : billing.rate) || 0);
    const taxPct = parseFloat(editForm.tax) || 0;
    const dates = [...editForm.datesCovered].sort();
    const n = dates.length;
    const assignedDates = editRow.workOrder?.inspectionDates || [];
    const workingDaysByMonth = billing.workingDaysByMonth || {};
    const assignedByMonth = new Map(groupDatesByMonth(assignedDates).map(g => [g.ym, g.count]));
    const perMonth = isManmonth
      ? groupDatesByMonth(dates).map(g => {
          const workingDays = parseFloat(workingDaysByMonth[g.ym]);
          const assignedCount = workingDays > 0 ? workingDays : (assignedByMonth.get(g.ym) || g.count);
          return {
            ...g,
            assignedCount,
            amount: parseFloat((rate > 0 && assignedCount > 0 ? (rate / assignedCount) * g.count : 0).toFixed(2)),
          };
        })
      : [];
    const subtotal = isManmonth
      ? parseFloat(monthlyShareAmount(dates, assignedDates, rate, workingDaysByMonth).toFixed(2))
      : parseFloat((rate * n).toFixed(2));
    const otTotal = parseFloat(editForm.overtime.reduce((s, ot) => s + overtimeEntryAmount(ot), 0).toFixed(2));
    const otherCosts = parseFloat(otherCostEntriesTotalOf(editForm.otherCostEntries).toFixed(2));
    const tax = parseFloat(((subtotal + otTotal) * taxPct / 100).toFixed(2));
    const total = parseFloat((subtotal + otTotal + tax + otherCosts).toFixed(2));
    return { rate, taxPct, dates, n, perMonth, isManmonth, subtotal, otTotal, otherCosts, tax, total };
  }, [editRow, editForm.datesCovered, editForm.tax, editForm.overtime, editForm.otherCostEntries]);

  const toggleSelected = (rowId) => setSelectedId(prev => prev === rowId ? null : rowId);

  const applyPayResult = (updated) => {
    setRows(prev => prev.map(r => r._id === updated._id
      ? {
          ...r,
          payableStatus: updated.payableStatus,
          paymentDate: updated.paymentDate,
          inspectorPaymentRate: updated.inspectorPaymentRate,
          inspectorPaymentTotal: updated.inspectorPaymentTotal,
          amountPaid: updated.amountPaid,
          tdsAmount: updated.tdsAmount,
          remainingAmount: updated.remainingAmount,
          payments: updated.payments,
        }
      : r));
  };

  const openEditModal = (row) => {
    setEditRowId(row._id);
    const billing = row.workOrder?.billing || {};
    setEditForm({
      invoiceNumber: row.invoiceNumber || '',
      invoiceDate: row.invoiceDate ? row.invoiceDate.slice(0, 10) : '',
      datesCovered: Array.isArray(row.datesCovered) ? row.datesCovered : [],
      tax: billing.tax ?? 0,
      otherCostEntries: Array.isArray(row.otherCostEntries) ? row.otherCostEntries : [],
      overtime: Array.isArray(row.overtime) ? row.overtime : [],
    });
  };

  const closeEditModal = () => {
    setEditRowId(null);
    setEditForm({ invoiceNumber: '', invoiceDate: '', datesCovered: [], tax: 0, otherCostEntries: [], overtime: [] });
  };

  // Overtime/Other Cost row editors — mirror handleAddInvoiceOvertime etc. in
  // src/pages/Manday.jsx, operating on local editForm state instead of inspectors[].
  const addEditOvertime = () => setEditForm(prev => ({ ...prev, overtime: [...prev.overtime, { dates: [], hours: '', rate: '', totalAmount: 0 }] }));
  const changeEditOvertime = (idx, updates) => setEditForm(prev => ({
    ...prev,
    overtime: prev.overtime.map((entry, i) => {
      if (i !== idx) return entry;
      const updated = { ...entry, ...updates };
      return { ...updated, totalAmount: parseFloat(overtimeEntryAmount(updated).toFixed(2)) };
    }),
  }));
  const removeEditOvertime = (idx) => setEditForm(prev => ({ ...prev, overtime: prev.overtime.filter((_, i) => i !== idx) }));
  const addEditOtherCost = () => setEditForm(prev => ({ ...prev, otherCostEntries: [...prev.otherCostEntries, { description: '', amount: '' }] }));
  const changeEditOtherCost = (idx, field, value) => setEditForm(prev => ({
    ...prev,
    otherCostEntries: prev.otherCostEntries.map((entry, i) => i === idx ? { ...entry, [field]: value } : entry),
  }));
  const removeEditOtherCost = (idx) => setEditForm(prev => ({ ...prev, otherCostEntries: prev.otherCostEntries.filter((_, i) => i !== idx) }));

  const submitEditInvoice = async () => {
    const row = rows.find(r => r._id === editRowId);
    if (!row) return;
    if (!editForm.invoiceNumber) return toast.info('Invoice number is required');
    setEditSaving(true);
    const result = await apiRequest(
      `${API_URLS.FINANCE_PAYABLES}/${row.enquiryId}/invoices/${row._id}/edit`,
      {
        method: 'PATCH',
        body: {
          invoiceNumber: editForm.invoiceNumber,
          invoiceDate: editForm.invoiceDate,
          datesCovered: editForm.datesCovered,
          // Per-invoice — always sent, no cross-invoice lock needed here.
          overtime: editForm.overtime,
          otherCostEntries: editForm.otherCostEntries,
          // Tax Rate is still shared/work-order-level — only sent while unlocked, so a
          // paid sibling invoice can't make the server reject the whole save.
          ...(editBillingLocked ? {} : { tax: editForm.tax }),
        },
      },
      token
    );
    if (result?.ok) {
      // updatedSiblingInvoices covers every invoice on this work order — a Tax edit
      // moves all of their shares at once, so every visible row needs refreshing.
      const bySiblingId = new Map((result.data.updatedSiblingInvoices || []).map(s => [String(s._id), s]));
      setRows(prev => prev.map(r => {
        if (r._id === row._id) {
          const sibling = bySiblingId.get(String(r._id));
          return {
            ...r,
            invoiceNumber: result.data.invoiceNumber,
            invoiceDate: result.data.invoiceDate,
            datesCovered: result.data.datesCovered,
            daysCovered: result.data.daysCovered,
            overtime: result.data.overtime,
            otherCostEntries: result.data.otherCostEntries,
            invoiceAmount: sibling?.invoiceAmount ?? result.data.invoiceAmount,
            remainingAmount: sibling?.remainingAmount ?? result.data.remainingAmount,
            workOrder: result.data.billing ? { ...r.workOrder, billing: result.data.billing } : r.workOrder,
          };
        }
        const sibling = bySiblingId.get(String(r._id));
        if (!sibling) return r;
        return {
          ...r,
          invoiceAmount: sibling.invoiceAmount,
          remainingAmount: sibling.remainingAmount,
          workOrder: result.data.billing ? { ...r.workOrder, billing: result.data.billing } : r.workOrder,
        };
      }));
      closeEditModal();
    } else {
      toast.error(result?.data?.message || 'Could not save changes');
    }
    setEditSaving(false);
  };

  const openPayModal = () => {
    const row = rows.find(r => r._id === selectedId);
    const today = new Date().toISOString().split('T')[0];
    // Smart default, fetched fresh every time the modal opens — deliberately doesn't
    // carry forward row.inspectorPaymentRate (a previous payment's rate); each payment
    // is its own event and may fall on a different date with a different rate.
    const historyRate = row ? rateFromHistoryAsOf(rateHistory, row.currencyCost, today) : null;
    const defaultRate = row ? (historyRate ?? row.invoicingRate ?? defaultInvoicingRate(row)) : '';
    setPayForm({ paymentDate: today, inspectorPaymentRate: defaultRate, tdsAmount: '' });
    setPayRateTouched(false); // always start untouched so the fresh default can track the date
    setShowPayModal(true);
  };

  const submitPay = async () => {
    if (!selectedId) return;
    const row = rows.find(r => r._id === selectedId);
    if (!row) return;
    if (!payForm.paymentDate) return toast.info('Payment date is required');
    const tdsAmount = parseFloat(payForm.tdsAmount) || 0;
    const remaining = parseFloat(row.remainingAmount) || 0;
    if (tdsAmount < 0) return toast.info('TDS amount cannot be negative');
    if (tdsAmount > remaining + 0.005) return toast.info(`TDS cannot exceed the balance (${fmt(remaining)})`);
    setPaying(true);
    const inspectorPaymentRate = payForm.inspectorPaymentRate === '' ? null : parseFloat(payForm.inspectorPaymentRate);
    // inspectorPaymentTotal (net cash × rate) is computed server-side.
    const result = await apiRequest(
      `${API_URLS.FINANCE_PAYABLES}/pay`,
      {
        method: 'PATCH',
        body: {
          items: [{ enquiryId: row.enquiryId, invoiceId: row._id }],
          paymentDate: payForm.paymentDate,
          inspectorPaymentRate,
          tdsAmount,
        },
      },
      token
    );
    if (result?.ok) {
      applyPayResult(result.data.updated[0]);
      setSelectedId(null);
      setShowPayModal(false);
    } else {
      toast.error(result?.data?.message || 'Could not record payment');
    }
    setPaying(false);
  };

  const openPartialPayModal = () => {
    const row = rows.find(r => r._id === selectedId);
    const today = new Date().toISOString().split('T')[0];
    // See openPayModal above — matters even more here since one invoice can take
    // several partial payments over time, each its own event.
    const historyRate = row ? rateFromHistoryAsOf(rateHistory, row.currencyCost, today) : null;
    const defaultRate = row ? (historyRate ?? row.invoicingRate ?? defaultInvoicingRate(row)) : '';
    setPartialPayForm({ paymentDate: today, inspectorPaymentRate: defaultRate, amount: '', tdsAmount: '' });
    setPartialPayRateTouched(false); // always start untouched so the fresh default can track the date
    setShowPartialPayModal(true);
  };

  const submitPartialPay = async () => {
    if (!selectedId) return;
    const row = rows.find(r => r._id === selectedId);
    if (!row) return;
    if (!partialPayForm.paymentDate) return toast.info('Payment date is required');
    const grossAmount = parseFloat(partialPayForm.amount);
    if (!(grossAmount > 0)) return toast.info('Enter a valid amount to settle');
    const tdsAmount = parseFloat(partialPayForm.tdsAmount) || 0;
    const remaining = parseFloat(row.remainingAmount) || 0;
    if (tdsAmount < 0) return toast.info('TDS amount cannot be negative');
    if (tdsAmount > grossAmount + 0.005) return toast.info('TDS cannot exceed the amount being settled');
    if (grossAmount > remaining + 0.005) return toast.info(`Amount cannot exceed the balance (${fmt(remaining)})`);
    setPartialPaying(true);
    const inspectorPaymentRate = partialPayForm.inspectorPaymentRate === '' ? null : parseFloat(partialPayForm.inspectorPaymentRate);
    // Server takes `amount` as cash only; inspectorPaymentTotal (cash × rate) is computed there.
    const result = await apiRequest(
      `${API_URLS.FINANCE_PAYABLES}/pay`,
      {
        method: 'PATCH',
        body: {
          items: [{ enquiryId: row.enquiryId, invoiceId: row._id }],
          paymentDate: partialPayForm.paymentDate,
          inspectorPaymentRate,
          partial: true,
          amount: +(grossAmount - tdsAmount).toFixed(2),
          tdsAmount,
        },
      },
      token
    );
    if (result?.ok) {
      applyPayResult(result.data.updated[0]);
      setSelectedId(null);
      setShowPartialPayModal(false);
    } else {
      toast.error(result?.data?.message || 'Could not record partial payment');
    }
    setPartialPaying(false);
  };

  const fetchPayables = useCallback(async () => {
    setLoading(true);
    const result = await apiRequest(API_URLS.FINANCE_PAYABLES, {}, token);
    if (result?.ok) setRows(result.data.rows || []);
    setLoading(false);
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    // PAYMENT_RECEIVED: customer paid. INVOICE_AGED: 45 days since the inspector's own
    // invoice date. Both are just triggers onto the same "Ready to Payable" action.
    const result = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}?type=PAYMENT_RECEIVED,INVOICE_AGED&isRead=false&_t=${Date.now()}`, {}, token);
    if (result?.ok) setNotifications(result.data.rows || []);
  }, [token]);

  useEffect(() => { fetchPayables(); }, [fetchPayables]);
  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleDismissNotif = async (notif) => {
    const result = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/${notif._id}/read`, { method: 'PUT' }, token);
    if (result?.ok) {
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    } else {
      toast.error(result?.data?.message || 'Could not dismiss notification');
    }
  };

  // Marks the inspector invoice behind a payment-received notification as Payable,
  // then drops that entry from the notification list since it's been acted on.
  const handleMarkNotifPayable = async (notif) => {
    setNotifActioning(prev => ({ ...prev, [notif._id]: true }));
    const result = await apiRequest(
      `${API_URLS.FINANCE_PAYABLES}/${notif.enquiryId}/invoices/${notif.payableInvoiceId}`,
      { method: 'PATCH', body: { payableStatus: 'Payable' } },
      token
    );
    if (result?.ok) {
      setRows(prev => prev.map(r => r._id === notif.payableInvoiceId ? { ...r, payableStatus: 'Payable' } : r));
      await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/${notif._id}/read`, { method: 'PUT' }, token);
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    } else {
      toast.error(result?.data?.message || 'Could not mark inspector as payable');
    }
    setNotifActioning(prev => ({ ...prev, [notif._id]: false }));
  };

  const unlockRate = (row) => {
    setUnlocked(prev => ({ ...prev, [row._id]: true }));
    setDrafts(prev => ({ ...prev, [row._id]: row.invoicingRate ?? defaultInvoicingRate(row) }));
  };

  const cancelRateEdit = (rowId) => {
    setUnlocked(prev => { const n = { ...prev }; delete n[rowId]; return n; });
    setDrafts(prev => { const n = { ...prev }; delete n[rowId]; return n; });
  };

  const confirmRateUpdate = (row) => {
    const newRate = drafts[row._id];
    toast.confirm(`Update invoicing rate to ${newRate}? This recalculates the invoice total.`, async () => {
      setRateUpdating(prev => ({ ...prev, [row._id]: true }));
      const result = await apiRequest(
        `${API_URLS.FINANCE_PAYABLES}/${row.enquiryId}/invoices/${row._id}`,
        { method: 'PATCH', body: { invoicingRate: newRate === '' ? null : parseFloat(newRate) } },
        token
      );
      if (result?.ok) {
        setRows(prev => prev.map(r => r._id === row._id ? { ...r, invoicingRate: result.data.invoicingRate } : r));
      } else {
        toast.error(result?.data?.message || 'Could not update invoicing rate');
      }
      setRateUpdating(prev => ({ ...prev, [row._id]: false }));
      cancelRateEdit(row._id);
    });
  };

  const fmt = n => n != null ? (+n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  // A row with a remaining balance still owed stays actionable under "Payable" even
  // once it's flipped to "Partial Paid" — its badge shows Partial Paid, but it keeps
  // counting/filtering under Payable too so the leftover doesn't get lost from view.
  const matchesStatus = (r, s) => {
    const status = r.payableStatus || 'Booked';
    if (s === 'Payable') return status === 'Payable' || (status === 'Partial Paid' && (r.remainingAmount || 0) > 0);
    return status === s;
  };

  const statusCounts = PAYABLE_STATUSES.reduce((acc, s) => {
    acc[s] = rows.filter(r => matchesStatus(r, s)).length;
    return acc;
  }, {});

  // Search across every field shown in the table — job/customer/inspector/invoice
  // number, currency, rate/cost/total figures, status, and the work order number.
  const matchesSearch = (r, q) => {
    const workOrderTotal = (parseFloat(r.invoiceAmount) || 0);
    const effectiveRate = r.invoicingRate ?? defaultInvoicingRate(r);
    const haystack = [
      r.jobNumber, r.customerName, inspectorDisplayName(r.inspectorAgency), r.invoiceNumber,
      r.currencyCost, r.payableStatus, r.workOrder?.number, r.cost, effectiveRate,
      workOrderTotal, workOrderTotal * effectiveRate,
    ].filter(v => v != null).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  // Header filter option lists — derived straight from whatever's already loaded
  // (this page has no server pagination, so there's no separate lookup to fetch).
  const companyOptions = useMemo(() => (
    [...new Map(rows.filter(r => r.companyId).map(r => [String(r.companyId), r.companyName])).entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [rows]);
  const coordinatorOptions = useMemo(() => (
    [...new Map(rows.filter(r => r.coordinatorId).map(r => [String(r.coordinatorId), r.coordinatorName])).entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [rows]);
  const inspectorOptions = useMemo(() => (
    [...new Map(rows.filter(r => r.inspectorAgency).map(r => [String(r.inspectorId || r.inspectorAgency), r.inspectorAgency])).entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [rows]);
  const jobNumberOptions = useMemo(() => (
    [...new Set(rows.map(r => r.jobNumber).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map(jn => ({ id: jn, label: jn }))
  ), [rows]);

  const filteredRows = rows
    .filter(r => !statusFilter || matchesStatus(r, statusFilter))
    .filter(r => !search.trim() || matchesSearch(r, search.trim().toLowerCase()))
    .filter(r => !companyFilter || String(r.companyId) === companyFilter)
    .filter(r => !coordinatorFilter || String(r.coordinatorId) === coordinatorFilter)
    .filter(r => !inspectorFilter || String(r.inspectorId || r.inspectorAgency) === inspectorFilter)
    .filter(r => !jobNumberFilter || r.jobNumber === jobNumberFilter)
    .filter(r => !monthFilter || (r.invoiceDate && new Date(r.invoiceDate).toISOString().slice(0, 7) === monthFilter));

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit));
  const pagedRows = filteredRows.slice((page - 1) * limit, page * limit);

  // Pay/Partial Pay only make sense for a row actually in Payable status — the
  // checkbox itself stays selectable on every row regardless.
  const selectedRow = rows.find(r => r._id === selectedId);
  const canPaySelected = !!selectedRow && matchesStatus(selectedRow, 'Payable');

  return (
    <div className="fin-page">
      <div className="fin-card">
        <div className="fin-header">
          <div>
            <h2 className="fin-title">Inspector Invoice</h2>
            <p className="fin-subtitle">{filteredRows.length} of {rows.length} inspector invoices</p>
          </div>
          <div className="fin-controls">
            <div className="fin-search-wrap">
              <FiSearch size={14} />
              <input className="fin-search-input" placeholder="Search job, customer, inspector, invoice…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <button className="fin-btn fin-btn-outline" style={{ position: 'relative' }} title="Payment notifications" onClick={() => setShowNotifModal(true)}>
              <FiBell size={14} />
              {notifications.length > 0 && (
                <span className="fin-notif-count" style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, fontSize: 10 }}>
                  {notifications.length}
                </span>
              )}
            </button>
            <FilterPanel
              values={{
                company: companyFilter,
                coordinator: coordinatorFilter,
                inspector: inspectorFilter,
                jobNumber: jobNumberFilter,
                month: monthFilter,
              }}
              emptyValues={{ company: '', coordinator: '', inspector: '', jobNumber: '', month: '' }}
              onChange={(v) => {
                setCompanyFilter(v.company);
                setCoordinatorFilter(v.coordinator);
                setInspectorFilter(v.inspector);
                setJobNumberFilter(v.jobNumber);
                setMonthFilter(v.month);
                setPage(1);
              }}
              width={300}
            >
              {(filters, set) => (
                <>
                  <FilterField label="Company">
                    <Select
                      options={[
                        { value: '', label: 'All Companies' },
                        ...companyOptions.map(c => ({ value: c.id, label: c.name })),
                      ]}
                      value={{ value: filters.company, label: companyOptions.find(c => c.id === filters.company)?.name || 'All Companies' }}
                      onChange={opt => set({ company: opt?.value || '' })}
                      styles={filterSelectStyles}
                      isClearable={false}
                      placeholder="Search company…"
                    />
                  </FilterField>
                  <FilterField label="Coordinator">
                    <Select
                      options={[
                        { value: '', label: 'All Coordinators' },
                        ...coordinatorOptions.map(c => ({ value: c.id, label: c.name })),
                      ]}
                      value={{ value: filters.coordinator, label: coordinatorOptions.find(c => c.id === filters.coordinator)?.name || 'All Coordinators' }}
                      onChange={opt => set({ coordinator: opt?.value || '' })}
                      styles={filterSelectStyles}
                      isClearable={false}
                      placeholder="Search coordinator…"
                    />
                  </FilterField>
                  <FilterField label="Inspector">
                    <Select
                      options={[
                        { value: '', label: 'All Inspectors' },
                        ...inspectorOptions.map(o => ({ value: o.id, label: o.label })),
                      ]}
                      value={{ value: filters.inspector, label: inspectorOptions.find(o => o.id === filters.inspector)?.label || 'All Inspectors' }}
                      onChange={opt => set({ inspector: opt?.value || '' })}
                      styles={filterSelectStyles}
                      isClearable={false}
                      placeholder="Search inspector…"
                    />
                  </FilterField>
                  <FilterField label="Job Number">
                    <Select
                      options={[
                        { value: '', label: 'All Job Numbers' },
                        ...jobNumberOptions.map(o => ({ value: o.id, label: o.label })),
                      ]}
                      value={{ value: filters.jobNumber, label: filters.jobNumber || 'All Job Numbers' }}
                      onChange={opt => set({ jobNumber: opt?.value || '' })}
                      styles={filterSelectStyles}
                      isClearable={false}
                      placeholder="Search job number…"
                    />
                  </FilterField>
                  <FilterField label="Invoice Month">
                    <div className="fin-month-wrap" style={{ width: '100%' }}>
                      <input
                        type="month"
                        className={`fin-month-input${filters.month ? ' has-value' : ''}`}
                        value={filters.month}
                        onChange={e => set({ month: e.target.value })}
                      />
                      {!filters.month && <span className="fin-month-placeholder" style={{ color: '#999' }}>All Months</span>}
                    </div>
                  </FilterField>
                </>
              )}
            </FilterPanel>
            {canPaySelected && (
              <>
                <button className="fin-btn fin-btn-success" onClick={openPayModal}>Pay</button>
                <button className="fin-btn fin-btn-warning" onClick={openPartialPayModal}>Partial Pay</button>
              </>
            )}
            <button className="fin-btn fin-btn-outline" onClick={() => { setSelectedId(null); fetchPayables(); }}><FiRefreshCw size={14} /></button>
          </div>
        </div>

        <div style={{ padding: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {PAYABLE_STATUSES.map(s => {
            const active = statusFilter === s;
            const style = payableStatusStyle(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => { setStatusFilter(active ? '' : s); setPage(1); }}
                style={{
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 12,
                  gap: 6,
                  cursor: 'pointer',
                  border: '1px solid #ccc',
                  background: '#fff',
                  fontWeight: active ? 600 : 400,
                  transform: active ? 'translateY(-3px)' : 'translateY(0)',
                  boxShadow: active ? '0 6px 16px rgba(0,0,0,0.25)' : 'none',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                {s}
                <span style={{ ...style, borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>
                  {statusCounts[s]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="fin-table-wrap" style={{ border: 0 }}>
          <table className="vendor-table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th></th>
                <th>Job Number</th>
                <th>Customer</th>
                <th>Inspector Name</th>
                <th>Invoice Number</th>
                <th>Inspector Invoicing Rate</th>
                <th style={{ textAlign: 'right' }}>Invoice Total</th>
                <th style={{ textAlign: 'right' }}>Total (INR)</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="fin-loading">Loading…</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={10} className="fin-empty">{statusFilter ? `No "${statusFilter}" inspector invoices` : 'No uploaded inspector invoices'}</td></tr>
              ) : pagedRows.map(r => {
                const savedRate = r.invoicingRate ?? defaultInvoicingRate(r);
                const isUnlocked = !!unlocked[r._id];
                const isBusy = !!rateUpdating[r._id];
                const effectiveRate = isUnlocked ? (parseFloat(drafts[r._id]) || 0) : savedRate;
                // r.invoiceAmount is this invoice's own share of the work order, computed
                // server-side in payableController.
                const workOrderTotal = parseFloat(r.invoiceAmount) || 0;
                const totalINR = workOrderTotal * effectiveRate;
                // Highlight rows not yet fully paid — green once the customer has paid,
                // else blue once 45 days past the invoice date.
                const rowClass = r.payableStatus === 'Paid' ? ''
                  : r.customerPaymentReceived ? 'fin-row-customer-paid'
                  : r.isAged ? 'fin-row-aged' : '';
                return (
                  <tr key={r._id} className={rowClass}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedId === r._id}
                        onChange={() => toggleSelected(r._id)}
                        title="Select for payment"
                      />
                    </td>
                    <td>{r.jobNumber || '—'}</td>
                    <td>{r.customerName || '—'}</td>
                    <td>{inspectorDisplayName(r.inspectorAgency) || '—'}</td>
                    <td>{r.invoiceNumber || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                        <input
                          type="number"
                          step="0.0001"
                          value={isUnlocked ? drafts[r._id] ?? savedRate : savedRate}
                          disabled={!isUnlocked || isBusy}
                          onChange={e => setDrafts(prev => ({ ...prev, [r._id]: e.target.value }))}
                          style={{
                            width: 70, padding: '2px 4px', fontSize: 11,
                            border: `1px solid ${isUnlocked ? '#ccc' : '#d1d5db'}`,
                            background: isUnlocked ? '#fff' : '#f3f4f6',
                            color: isUnlocked ? '#111' : '#6b7280',
                            cursor: isUnlocked ? 'text' : 'not-allowed',
                            opacity: isBusy ? 0.5 : 1,
                          }}
                        />
                        {isUnlocked ? (
                          <>
                            <button type="button" title="Confirm update" disabled={isBusy}
                              onClick={() => confirmRateUpdate(r)}
                              style={{ background: 'none', border: 'none', color: '#16a34a', cursor: isBusy ? 'not-allowed' : 'pointer', padding: 2, display: 'flex' }}>
                              <FiCheck size={12} />
                            </button>
                            <button type="button" title="Cancel" disabled={isBusy}
                              onClick={() => cancelRateEdit(r._id)}
                              style={{ background: 'none', border: 'none', color: '#dc2626', cursor: isBusy ? 'not-allowed' : 'pointer', padding: 2, display: 'flex' }}>
                              <FiX size={12} />
                            </button>
                          </>
                        ) : (
                          <button type="button" title="Unlock to edit"
                            onClick={() => unlockRate(r)}
                            style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', padding: 2, display: 'flex' }}>
                            <FiEdit size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {workOrderTotal
                        ? `${r.currencyCost || 'INR'} ${fmt(workOrderTotal)}`
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{fmt(totalINR)}</td>
                    <td>
                      <span
                        title="Not manually editable — Payable is only set via a Payment Notification (customer paid, or 45 days since invoice date); Paid/Partial Paid only via Pay/Partial Pay"
                        style={{
                          ...payableStatusStyle(r.payableStatus),
                          display: 'inline-block', borderRadius: 4, padding: '3px 8px',
                          fontSize: 11, fontWeight: 600,
                        }}
                      >
                        {r.payableStatus || 'Booked'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <Link to={`/enquiry/manday/${r.enquiryId}?from=inspection-invoice`} target="_blank" title="View Enquiry"
                          style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }}>
                          <FiExternalLink size={14} />
                        </Link>
                        {(r.workOrder?.url || r.workOrder?.signedUrl)
                          ? <a href={r.workOrder.url || r.workOrder.signedUrl} target="_blank" rel="noopener noreferrer" title="Work Order"
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }}>
                              <FiFile size={14} />
                            </a>
                          : <span title="No work order document" style={{ border: '1px solid #e5e7eb', borderRadius: 4, color: '#ccc', padding: 4, display: 'flex' }}>
                              <FiFile size={14} />
                            </span>}
                        {r.workOrder?.number
                          ? <button type="button" title="Work Order Details" onClick={() => setWorkOrderDetailRowId(r._id)}
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }}>
                              <FiInfo size={14} />
                            </button>
                          : <span title="No work order" style={{ border: '1px solid #e5e7eb', borderRadius: 4, color: '#ccc', padding: 4, display: 'flex' }}>
                              <FiInfo size={14} />
                            </span>}
                        {r.fileUrl
                          ? <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" title="Inspection Invoice"
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }}>
                              <FaFileInvoiceDollar size={14} />
                            </a>
                          : <span title="No invoice file" style={{ border: '1px solid #e5e7eb', borderRadius: 4, color: '#ccc', padding: 4, display: 'flex' }}>
                              <FaFileInvoiceDollar size={14} />
                            </span>}
                        {r.payments?.length > 0
                          ? <button type="button" title="Payment History" onClick={() => setHistoryRowId(r._id)}
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }}>
                              <FiClock size={14} />
                            </button>
                          : <span title="No payment history yet" style={{ border: '1px solid #e5e7eb', borderRadius: 4, color: '#ccc', padding: 4, display: 'flex' }}>
                              <FiClock size={14} />
                            </span>}
                        {(parseFloat(r.amountPaid) || 0) === 0
                          ? <button type="button" title="Edit Invoice" onClick={() => openEditModal(r)}
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }}>
                              <FiEdit size={14} />
                            </button>
                          : <span title="Locked — a payment is already recorded" style={{ border: '1px solid #e5e7eb', borderRadius: 4, color: '#ccc', padding: 4, display: 'flex' }}>
                              <FiEdit size={14} />
                            </span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredRows.length > 0 && (
          <div className="cust-pagination">
            <select
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
            >
              {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="cust-page-info">Page {page} of {totalPages}</span>
            <button className="cust-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <button className="cust-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Pay Modal */}
      {showPayModal && (() => {
        const row = rows.find(r => r._id === selectedId);
        const paymentRate = row ? (row.invoicingRate ?? defaultInvoicingRate(row)) : 0;
        const workOrderTotal = parseFloat(row?.invoiceAmount) || 0; // this invoice's own share, not the full work order
        const totalINR = workOrderTotal * paymentRate;
        const inspectorPaymentTotal = workOrderTotal * (parseFloat(payForm.inspectorPaymentRate) || 0);
        const remaining = parseFloat(row?.remainingAmount) || 0; // full pay settles whatever is still owed
        const tdsAmount = parseFloat(payForm.tdsAmount) || 0;
        const netPayable = Math.max(0, remaining - tdsAmount);
        const netPayableINR = netPayable * (parseFloat(payForm.inspectorPaymentRate) || 0);
        const cur = row?.currencyCost || 'INR';
        const readOnlyField = (label, value) => (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
            <input type="text" className="fin-input" style={{ width: '100%', background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} value={value} disabled />
          </div>
        );
        return (
          <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !paying) setShowPayModal(false); }}>
            <div className="fin-modal" style={{ maxWidth: "85%" }}>
              <div className="fin-modal-header">
                <h3 className="fin-modal-title">Inspector Payment</h3>
                <button className="fin-modal-close" disabled={paying} onClick={() => setShowPayModal(false)}><FiX /></button>
              </div>
              <div className="fin-modal-body" style={{ display : "grid" , gridTemplateColumns : "repeat(4,1fr)" , gap : 16 }}>
                {row && (
                  <>
                    {readOnlyField('Job Number', row.jobNumber || '—')}
                    {readOnlyField('Inspector Name', inspectorDisplayName(row.inspectorAgency) || '—')}
                    {readOnlyField('Invoice Number', row.invoiceNumber || '—')}
                    {readOnlyField('Cost', `${row.currencyCost || 'INR'} ${fmt(row.cost)}`)}
                    {readOnlyField('Other Cost', fmt(row.workOrder?.billing?.otherCosts))}
                    {readOnlyField('Tax %', row.workOrder?.billing?.tax != null ? `${fmt(row.workOrder.billing.tax)}%` : '—')}
                    {readOnlyField(
                      invoiceAmountLabel(row),
                      `${row.currencyCost || 'INR'} ${fmt(workOrderTotal)}`
                    )}
                    {readOnlyField('Invoice Payment Rate', fmt(paymentRate))}
                    {readOnlyField('Total (Invoice Amount × Invoice Payment Rate)', `₹${fmt(totalINR)}`)}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Inspector Payment Rate</label>
                      <input
                        type="number"
                        step="0.0001"
                        className="fin-input"
                        style={{ width: '100%' }}
                        value={payForm.inspectorPaymentRate}
                        onChange={e => { setPayRateTouched(true); setPayForm(prev => ({ ...prev, inspectorPaymentRate: e.target.value })); }}
                      />
                    </div>
                    {readOnlyField('Total (Invoice Amount × Inspector Payment Rate)', `₹${fmt(inspectorPaymentTotal)}`)}
                    {readOnlyField('Balance to Settle', `${cur} ${fmt(remaining)}`)}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>TDS Amount ({cur})</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="fin-input"
                        style={{ width: '100%' }}
                        value={payForm.tdsAmount}
                        onChange={e => setPayForm(prev => ({ ...prev, tdsAmount: e.target.value }))}
                      />
                    </div>
                    {readOnlyField('Net Payable to Inspector (Balance − TDS)', `${cur} ${fmt(netPayable)}`)}
                    {readOnlyField('Net Payable (INR, × Inspector Payment Rate)', `₹${fmt(netPayableINR)}`)}
                  </>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Payment Date *</label>
                  <input
                    type="date"
                    className="fin-input"
                    style={{ width: '100%' }}
                    value={payForm.paymentDate}
                    onChange={e => {
                      const newDate = e.target.value;
                      setPayForm(prev => {
                        // Smart default only — re-derives the rate unless manually typed.
                        if (payRateTouched || !row) return { ...prev, paymentDate: newDate };
                        const rate = rateFromHistoryAsOf(rateHistory, row.currencyCost, newDate);
                        return { ...prev, paymentDate: newDate, ...(rate != null ? { inspectorPaymentRate: rate } : {}) };
                      });
                    }}
                  />
                </div>
              </div>
              <div className="fin-modal-footer">
                <button className="fin-btn fin-btn-outline" disabled={paying} onClick={() => setShowPayModal(false)}>Cancel</button>
                <button className="fin-btn fin-btn-success" disabled={paying} onClick={submitPay}>{paying ? 'Paying…' : 'Confirm Payment'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Partial Pay Modal */}
      {showPartialPayModal && (() => {
        const row = rows.find(r => r._id === selectedId);
        const paymentRate = row ? (row.invoicingRate ?? defaultInvoicingRate(row)) : 0;
        const workOrderTotal = parseFloat(row?.invoiceAmount) || 0; // this invoice's own share, not the full work order
        const totalINR = workOrderTotal * paymentRate;
        const inspectorPaymentTotal = workOrderTotal * (parseFloat(partialPayForm.inspectorPaymentRate) || 0);
        const alreadyPaid = parseFloat(row?.amountPaid) || 0;
        const enteredAmount = parseFloat(partialPayForm.amount) || 0; // gross settled now (cash + TDS)
        const partialTds = parseFloat(partialPayForm.tdsAmount) || 0;
        const netCash = enteredAmount - partialTds;
        const netCashINR = netCash * (parseFloat(partialPayForm.inspectorPaymentRate) || 0);
        const remaining = workOrderTotal - alreadyPaid - enteredAmount;
        const cur = row?.currencyCost || 'INR';
        const readOnlyField = (label, value, negative = false) => (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
            <input
              type="text"
              className="fin-input"
              style={{ width: '100%', background: '#f3f4f6', color: negative ? '#dc2626' : '#6b7280', fontWeight: negative ? 700 : 400, cursor: 'not-allowed' }}
              value={value}
              disabled
            />
          </div>
        );
        return (
          <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !partialPaying) setShowPartialPayModal(false); }}>
            <div className="fin-modal" style={{ maxWidth:"85%" }}>
              <div className="fin-modal-header">
                <h3 className="fin-modal-title">Inspector Partial Payment</h3>
                <button className="fin-modal-close" disabled={partialPaying} onClick={() => setShowPartialPayModal(false)}><FiX /></button>
              </div>
              <div className="fin-modal-body" style={{ display : "grid" , gridTemplateColumns : "repeat(4,1fr)" , gap : 16 }}>
                {row && (
                  <>
                    {readOnlyField('Job Number', row.jobNumber || '—')}
                    {readOnlyField('Inspector Name', inspectorDisplayName(row.inspectorAgency) || '—')}
                    {readOnlyField('Invoice Number', row.invoiceNumber || '—')}
                    {readOnlyField(
                      invoiceAmountLabel(row),
                      `${row.currencyCost || 'INR'} ${fmt(workOrderTotal)}`
                    )}
                    {readOnlyField('Invoice Payment Rate', fmt(paymentRate))}
                    {readOnlyField('Total (Invoice Amount × Invoice Payment Rate)', `₹${fmt(totalINR)}`)}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Inspector Payment Rate</label>
                      <input
                        type="number"
                        step="0.0001"
                        className="fin-input"
                        style={{ width: '100%' }}
                        value={partialPayForm.inspectorPaymentRate}
                        onChange={e => { setPartialPayRateTouched(true); setPartialPayForm(prev => ({ ...prev, inspectorPaymentRate: e.target.value })); }}
                      />
                    </div>
                    {readOnlyField('Total (Invoice Amount × Inspector Payment Rate)', `₹${fmt(inspectorPaymentTotal)}`)}
                    {readOnlyField('Already Settled (incl. TDS)', `${cur} ${fmt(alreadyPaid)}`)}
                    {readOnlyField('Remaining After This', `${cur} ${fmt(remaining)}`, remaining < 0)}
                  </>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Amount to Settle Now ({cur}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="fin-input"
                    style={{ width: '100%' }}
                    value={partialPayForm.amount}
                    onChange={e => setPartialPayForm(prev => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>TDS Amount ({cur})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="fin-input"
                    style={{ width: '100%' }}
                    value={partialPayForm.tdsAmount}
                    onChange={e => setPartialPayForm(prev => ({ ...prev, tdsAmount: e.target.value }))}
                  />
                </div>
                {readOnlyField('Net Cash to Inspector (Amount − TDS)', `${cur} ${fmt(netCash)}`, netCash < 0)}
                {readOnlyField('Net Cash (INR, × Inspector Payment Rate)', `₹${fmt(netCashINR)}`, netCashINR < 0)}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Payment Date *</label>
                  <input
                    type="date"
                    className="fin-input"
                    style={{ width: '100%' }}
                    value={partialPayForm.paymentDate}
                    onChange={e => {
                      const newDate = e.target.value;
                      setPartialPayForm(prev => {
                        // Smart default only — see the Pay modal's Payment Date handler above.
                        if (partialPayRateTouched || !row) return { ...prev, paymentDate: newDate };
                        const rate = rateFromHistoryAsOf(rateHistory, row.currencyCost, newDate);
                        return { ...prev, paymentDate: newDate, ...(rate != null ? { inspectorPaymentRate: rate } : {}) };
                      });
                    }}
                  />
                </div>
              </div>
              <div className="fin-modal-footer">
                <button className="fin-btn fin-btn-outline" disabled={partialPaying} onClick={() => setShowPartialPayModal(false)}>Cancel</button>
                <button className="fin-btn fin-btn-warning" disabled={partialPaying} onClick={submitPartialPay}>{partialPaying ? 'Paying…' : 'Confirm Partial Payment'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment History Modal */}
      {historyRowId && (() => {
        const row = rows.find(r => r._id === historyRowId);
        if (!row) return null;
        return (
          <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setHistoryRowId(null); }}>
            <div className="fin-modal" style={{ maxWidth: "60%" }}>
              <div className="fin-modal-header">
                <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' }}>
                  <FiClock size={18} style={{ marginRight: 6 }} /> Payment History - {inspectorDisplayName(row.inspectorAgency) || '—'}
                </h3>
                <button className="fin-modal-close" onClick={() => setHistoryRowId(null)}><FiX /></button>
              </div>
              <div className="fin-modal-body">
                <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                  Job No. {row.jobNumber || '—'} &bull; Invoice {row.invoiceNumber || '—'} &bull; Total Settled: {row.currencyCost || 'INR'} {fmt(row.amountPaid)}
                  {(row.tdsAmount || 0) > 0 && <> &bull; TDS Deducted: {row.currencyCost || 'INR'} {fmt(row.tdsAmount)}</>}
                </div>
                {(!row.payments || row.payments.length === 0) ? (
                  <p style={{ fontSize: 13, color: '#888' }}>No payments recorded yet.</p>
                ) : (
                  <div className="fin-table-wrap" style={{ border: 0 }}>
                    <table className="vendor-table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th style={{ textAlign: 'right' }}>Cash Paid</th>
                          <th style={{ textAlign: 'right' }}>TDS</th>
                          <th style={{ textAlign: 'right' }}>Settled</th>
                          <th style={{ textAlign: 'right' }}>Payment Rate</th>
                          <th style={{ textAlign: 'right' }}>Settled So far</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.payments.map((p, i) => (
                          <tr key={p._id || i}>
                            <td>{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-IN') : '—'}</td>
                            <td>{p.type || '—'}</td>
                            <td style={{ textAlign: 'right' }}>{fmt(p.amount)}</td>
                            <td style={{ textAlign: 'right' }}>
                              {p.tdsAmount ? fmt(p.tdsAmount) : '—'}
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmt((parseFloat(p.amount) || 0) + (parseFloat(p.tdsAmount) || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{p.inspectorPaymentRate != null ? fmt(p.inspectorPaymentRate) : '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(p.cumulativeAmountPaid)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="fin-modal-footer">
                <button className="fin-btn fin-btn-outline" onClick={() => setHistoryRowId(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit Invoice Modal — corrects Invoice Number/Date/Days Covered on an already-
          uploaded invoice. Only reachable while amountPaid is 0. */}
      {editRowId && editRow && (
        <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !editSaving) closeEditModal(); }}>
          <div className="fin-modal fin-modal-lg" style={{ maxHeight: '88vh' }}>
            <div className="fin-modal-header">
              <h3 className="fin-modal-title">Edit Invoice — {editRow.workOrder?.number || '—'}</h3>
              <button className="fin-modal-close" disabled={editSaving} onClick={closeEditModal}><FiX /></button>
            </div>
            <div className="fin-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Invoice Number *</label>
                  <input type="text" className="fin-input" style={{ width: '100%' }} value={editForm.invoiceNumber}
                    onChange={e => setEditForm(prev => ({ ...prev, invoiceNumber: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Invoice Date</label>
                  <input type="date" className="fin-input" style={{ width: '100%' }} value={editForm.invoiceDate}
                    onChange={e => setEditForm(prev => ({ ...prev, invoiceDate: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Days Covered</label>
                {editAvailableDates.length > 0 ? (
                  <DatePicker
                    multiple
                    format="DD-MM-YYYY"
                    value={editForm.datesCovered.map(d => new Date(d)).filter(d => !isNaN(d))}
                    onChange={(dates) => setEditForm(prev => ({ ...prev, datesCovered: dates ? dates.map(d => d.format('YYYY-MM-DD')) : [] }))}
                    mapDays={({ date }) => {
                      if (!editAvailableDates.includes(date.format('YYYY-MM-DD'))) return { disabled: true, style: { color: '#ccc' } };
                    }}
                    placeholder="No days covered set"
                    style={{ width: '100%', height: 36, border: '1px solid #d1d5db', borderRadius: 4, padding: '0 8px', boxSizing: 'border-box' }}
                  />
                ) : (
                  <div style={{ fontSize: 12, color: '#888' }}>No attended/unclaimed dates left on this work order to pick from.</div>
                )}
              </div>

              {/* Tax Rate — still work-order-level and shared, so editing it moves every
                  sibling invoice. Locked once any of them has a payment recorded. */}
              <div style={{ marginBottom: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
                {editBillingLocked && (
                  <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>
                    A payment is already recorded on this work order — Tax Rate can no longer be changed here.
                  </div>
                )}
                <fieldset disabled={editBillingLocked} style={{ border: 'none', padding: 0, margin: 0, maxWidth: 160 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Tax Rate (%)</label>
                  <input type="number" className="fin-input" style={{ width: '100%' }} value={editForm.tax}
                    onChange={e => setEditForm(prev => ({ ...prev, tax: e.target.value }))} />
                </fieldset>
              </div>

              {/* Overtime/Other Costs — specific to THIS invoice, no cross-invoice lock. */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <label style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>Overtime</label>
                        <button type="button" title="Add OT" style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }} onClick={addEditOvertime}>
                          <FiPlus size={12} />
                        </button>
                      </div>
                      {editForm.overtime.length === 0 ? (
                        <span style={{ color: '#888', fontSize: 12 }}>No overtime entries</span>
                      ) : (
                        <table className="vendor-table" style={{ fontSize: 11, width: '100%' }}>
                          <thead>
                            <tr><th>Date(s)</th><th style={{ width: 70 }}>Hours</th><th style={{ width: 70 }}>Rate</th><th style={{ width: 80, textAlign: 'right' }}>Amount</th><th style={{ width: 24 }}></th></tr>
                          </thead>
                          <tbody>
                            {editForm.overtime.map((entry, idx) => (
                              <tr key={idx}>
                                <td>
                                  <DatePicker
                                    multiple
                                    format="DD-MM-YYYY"
                                    value={(entry.dates || []).map(d => new Date(d)).filter(d => !isNaN(d))}
                                    onChange={(dates) => changeEditOvertime(idx, { dates: dates ? dates.map(d => d.format('YYYY-MM-DD')) : [] })}
                                    inputClass="fin-input"
                                    style={{ width: '100%', fontSize: 11 }}
                                    placeholder="Select date(s)"
                                  />
                                </td>
                                <td><input type="number" className="fin-input" style={{ fontSize: 11, width: '100%' }} value={entry.hours} onChange={(e) => changeEditOvertime(idx, { hours: e.target.value })} /></td>
                                <td><input type="number" className="fin-input" style={{ fontSize: 11, width: '100%' }} value={entry.rate} onChange={(e) => changeEditOvertime(idx, { rate: e.target.value })} /></td>
                                <td style={{ textAlign: 'right' }}>{editRow.currencyCost || 'INR'} {overtimeEntryAmount(entry).toFixed(2)}</td>
                                <td>
                                  <button type="button" title="Remove" onClick={() => removeEditOvertime(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                    <FiX size={12} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <label style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>Other Costs</label>
                        <button type="button" title="Add Other Cost" style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', padding: 4, display: 'flex' }} onClick={addEditOtherCost}>
                          <FiPlus size={12} />
                        </button>
                      </div>
                      {editForm.otherCostEntries.length === 0 ? (
                        <span style={{ color: '#888', fontSize: 12 }}>No other cost entries</span>
                      ) : (
                        <table className="vendor-table" style={{ fontSize: 11, width: '100%' }}>
                          <thead>
                            <tr><th>Description</th><th style={{ width: 90, textAlign: 'right' }}>Amount</th><th style={{ width: 24 }}></th></tr>
                          </thead>
                          <tbody>
                            {editForm.otherCostEntries.map((entry, idx) => (
                              <tr key={idx}>
                                <td><input type="text" className="fin-input" style={{ fontSize: 11, width: '100%' }} placeholder="e.g. Travel" value={entry.description} onChange={(e) => changeEditOtherCost(idx, 'description', e.target.value)} /></td>
                                <td><input type="number" className="fin-input" style={{ fontSize: 11, width: '100%', textAlign: 'right' }} value={entry.amount} onChange={(e) => changeEditOtherCost(idx, 'amount', e.target.value)} /></td>
                                <td>
                                  <button type="button" title="Remove" onClick={() => removeEditOtherCost(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                    <FiX size={12} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
              </div>

              {editBreakdown && editBreakdown.n > 0 && (
                <div style={{ paddingTop: 12, borderTop: '1px solid #eee' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                    Invoice Breakdown
                    {editBreakdown.isManmonth && (
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
                        (Monthly Rate ÷ days assigned that month × days this invoice covers)
                      </span>
                    )}
                  </label>
                  {editBreakdown.isManmonth ? (
                    <table className="vendor-table" style={{ fontSize: 12, width: '100%', marginBottom: 8 }}>
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th style={{ textAlign: 'right', width: 90 }}>Days Covered (this invoice)</th>
                          <th style={{ textAlign: 'right', width: 90 }}>Working Days (this month)</th>
                          <th style={{ textAlign: 'right', width: 90 }}>Monthly Rate</th>
                          <th style={{ textAlign: 'right', width: 110 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editBreakdown.perMonth.map(g => (
                          <tr key={g.ym}>
                            <td>{new Date(g.year, g.month - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</td>
                            <td style={{ textAlign: 'right' }}>{g.count}</td>
                            <td style={{ textAlign: 'right' }}>{g.assignedCount}</td>
                            <td style={{ textAlign: 'right' }}>{editRow.currencyCost || 'INR'} {fmt(editBreakdown.rate)}</td>
                            <td style={{ textAlign: 'right' }}>{editRow.currencyCost || 'INR'} {fmt(g.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="vendor-table" style={{ fontSize: 12, width: '100%', marginBottom: 8 }}>
                      <thead>
                        <tr><th>Date(s)</th><th style={{ textAlign: 'right', width: 90 }}>Rate / Day</th><th style={{ textAlign: 'right', width: 110 }}>Amount</th></tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{editBreakdown.dates.map(d => new Date(d).toLocaleDateString('en-IN')).join(', ')}</td>
                          <td style={{ textAlign: 'right' }}>{editRow.currencyCost || 'INR'} {fmt(editBreakdown.rate)}</td>
                          <td style={{ textAlign: 'right' }}>{editRow.currencyCost || 'INR'} {fmt(editBreakdown.subtotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, fontSize: 12 }}>
                    <div><span style={{ color: '#888' }}>{editBreakdown.isManmonth ? 'Prorated Amount' : 'Days × Rate'}</span><br />{editRow.currencyCost || 'INR'} {fmt(editBreakdown.subtotal)}</div>
                    <div><span style={{ color: '#888' }} title="This invoice's own Overtime entries (Overtime table above)">Overtime</span><br />{editRow.currencyCost || 'INR'} {fmt(editBreakdown.otTotal)}</div>
                    <div><span style={{ color: '#888' }} title="This invoice's own Other Cost entries (Other Costs table above)">Other Costs</span><br />{editRow.currencyCost || 'INR'} {fmt(editBreakdown.otherCosts)}</div>
                    <div><span style={{ color: '#888' }}>Tax ({editBreakdown.taxPct}%)</span><br />{editRow.currencyCost || 'INR'} {fmt(editBreakdown.tax)}</div>
                    <div style={{ fontWeight: 700 }}><span style={{ color: '#888', fontWeight: 400 }}>Invoice Amount</span><br />{editRow.currencyCost || 'INR'} {fmt(editBreakdown.total)}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="fin-modal-footer">
              <button className="fin-btn fin-btn-outline" disabled={editSaving} onClick={closeEditModal}>Cancel</button>
              <button className="fin-btn fin-btn-primary" disabled={editSaving} onClick={submitEditInvoice}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Work Order Details Modal — full read-only view of the work order (location,
          description, dates, billing breakdown incl. Other Costs/OT). Separate from the
          Pay/Partial Pay popups above, which stay focused on recording a payment. */}
      {workOrderDetailRowId && (() => {
        const row = rows.find(r => r._id === workOrderDetailRowId);
        if (!row) return null;
        const wo = row.workOrder || {};
        const billing = wo.billing || {};
        const admin = wo.administrative || {};
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';
        // Read-only field, styled like the disabled inputs in Manday.jsx's own Work
        // Order modal — same look as the actual work order.
        const roField = (label, value) => (
          <div className="form-group"><label>{label}</label><input type="text" className="form-control" value={value || value === 0 ? value : '—'} disabled /></div>
        );
        return (
          <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setWorkOrderDetailRowId(null); }}>
            <div className="fin-modal fin-modal-lg" style={{ maxHeight: '88vh' }}>
              <div className="fin-modal-header">
                <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' }}>
                  <FiInfo size={18} style={{ marginRight: 6 }} /> Work Order Details — {wo.number || '—'}
                </h3>
                <button className="fin-modal-close" onClick={() => setWorkOrderDetailRowId(null)}><FiX /></button>
              </div>
              <div className="fin-modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '15px', marginBottom: '20px' }}>
                  {roField('Work Order Number', wo.number)}
                  {roField('Inspector Name', inspectorDisplayName(row.inspectorAgency))}
                  {roField('Order Date', fmtDate(wo.date))}
                  {roField('Expected Start Date', fmtDate(wo.expectedStartDate))}
                  {roField('Expected End Date', fmtDate(wo.expectedEndDate))}
                  {roField('Work Location', wo.workLocation)}
                  <div className="form-group" style={{ gridColumn: 'span 3' }}><label>Issuing Company</label><textarea rows={2} className="form-control" value={wo.issuingCompany || '—'} disabled /></div>
                  <div className="form-group" style={{ gridColumn: 'span 3' }}><label>Work Description</label><textarea rows={2} className="form-control" value={wo.workDescription || '—'} disabled /></div>
                  <div className="form-group" style={{ gridColumn: 'span 3' }}><label>Additional Comments</label><textarea rows={2} className="form-control" value={wo.additionalComments || '—'} disabled /></div>
                </div>

                <div className="sub-card-header expanded" style={{ marginBottom: 0, cursor: 'default' }}><h3>BILLING DETAILS</h3></div>
                <div className="sub-card-content" style={{ marginBottom: '20px', padding: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px' }}>
                    {row.enquiryType === 'MANMONTH'
                      ? roField('Months', billing.months)
                      : roField('Days', billing.days)}
                    {row.enquiryType === 'MANMONTH'
                      ? roField('Monthly Rate', fmt(billing.monthlyRate))
                      : roField('Rate', fmt(billing.rate))}
                    <div className="form-group"><label>Amount</label><div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}><span style={{ position: 'absolute', left: 8, color: '#555', fontSize: 13 }}>{row.currencyCost || 'INR'}</span><input type="text" className="form-control" value={fmt(billing.amount)} disabled style={{ paddingLeft: 40 }} /></div></div>
                    {roField('Tax Rate (%)', billing.tax ?? 0)}
                    <div className="form-group"><label>Other Costs</label><div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}><span style={{ position: 'absolute', left: 8, color: '#555', fontSize: 13 }}>{row.currencyCost || 'INR'}</span><input type="text" className="form-control" value={fmt(billing.otherCosts)} disabled style={{ paddingLeft: 40 }} /></div></div>
                    <div className="form-group"><label>Total</label><div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}><span style={{ position: 'absolute', left: 8, color: '#555', fontSize: 13, fontWeight: 700 }}>{row.currencyCost || 'INR'}</span><input type="text" className="form-control" value={fmt(billing.total)} disabled style={{ paddingLeft: 40, backgroundColor: '#e9ecef', fontWeight: 'bold' }} /></div></div>
                  </div>
                </div>

                {/* A work order's total can be split across more than one invoice — list
                    every invoice raised against it here. Finance-specific, not part of
                    Manday.jsx's own Work Order modal. */}
                <div className="sub-card-header expanded" style={{ marginBottom: 0, cursor: 'default' }}><h3>INVOICES ON THIS WORK ORDER</h3></div>
                <div className="sub-card-content" style={{ marginBottom: '20px', padding: '1rem' }}>
                  <table className="vendor-table" style={{ fontSize: 12, width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: 11, padding: '8px 10px' }}>Invoice Number</th>
                        <th style={{ textAlign: 'right', fontSize: 11, padding: '8px 10px' }}>Days Covered</th>
                        <th style={{ textAlign: 'right', fontSize: 11, padding: '8px 10px' }}>Amount</th>
                        <th style={{ fontSize: 11, padding: '8px 10px' }}>Status</th>
                        <th style={{ textAlign: 'right', fontSize: 11, padding: '8px 10px' }}>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Manmonth has no billing.days of its own (it prorates instead) —
                        // fall back to the inspector's attended date count.
                        const plannedDays = billing.days || (wo.inspectionDates || []).filter(d => !(wo.absentDates || []).includes(d)).length;
                        return (
                          <>
                            <tr style={{ fontWeight: 700 }}>
                              <td>{row.invoiceNumber || '—'} (this row)</td>
                              <td style={{ textAlign: 'right' }}>{row.daysCovered || (plannedDays ? `${plannedDays} (full)` : '—')}</td>
                              <td style={{ textAlign: 'right' }}>{fmt(row.invoiceAmount)}</td>
                              <td>{row.payableStatus || 'Booked'}</td>
                              <td style={{ textAlign: 'right' }}>
                                {fmt(row.amountPaid)}
                                {(row.tdsAmount || 0) > 0 && <div style={{ fontSize: 10, color: '#92400e' }}>incl. TDS {fmt(row.tdsAmount)}</div>}
                              </td>
                            </tr>
                            {(row.siblingInvoices || []).map(sib => (
                              <tr key={sib._id}>
                                <td>{sib.invoiceNumber || '—'}</td>
                                <td style={{ textAlign: 'right' }}>{sib.daysCovered || (plannedDays ? `${plannedDays} (full)` : '—')}</td>
                                <td style={{ textAlign: 'right' }}>{fmt(sib.amount)}</td>
                                <td>{sib.payableStatus || 'Booked'}</td>
                                <td style={{ textAlign: 'right' }}>{fmt(sib.amountPaid)}</td>
                              </tr>
                            ))}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                <div className="sub-card-header expanded" style={{ marginBottom: 0, cursor: 'default' }}><h3>ADMINISTRATIVE DETAILS</h3></div>
                <div className="sub-card-content" style={{ padding: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '15px' }}>
                    {roField('Order Received By', admin.orderReceivedBy)}
                    {roField('Compiled By', admin.compiledBy)}
                    {roField('Approving Party', admin.approvingParty)}
                    {roField('Date of Approval', fmtDate(admin.dateOfApproval))}
                  </div>
                </div>
              </div>
              <div className="fin-modal-footer">
                <button className="fin-btn fin-btn-outline" onClick={() => setWorkOrderDetailRowId(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment Notifications Modal */}
      {showNotifModal && (
        <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowNotifModal(false); }}>
          <div className="fin-modal" style={{ maxWidth: 700 }}>
            <div className="fin-modal-header">
              <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' }}>
                <FiBell size={18} style={{ marginRight: 6 }} />
                {notifications.length} job{notifications.length === 1 ? '' : 's'} — ready to payable
              </h3>
              <button className="fin-modal-close" onClick={() => setShowNotifModal(false)}><FiX /></button>
            </div>
            <div className="fin-modal-body">
              {notifications.length === 0 ? (
                <p style={{ fontSize: 13, color: '#888' }}>No pending notifications.</p>
              ) : (
                <div className="fin-notif-list">
                  {notifications.map(n => (
                    <div key={n._id} className="fin-notif-item">
                      <div className="fin-notif-item-info">
                        <span className="fin-notif-job">Job No. {n.jobNumber}</span>
                        <span className="fin-notif-meta">
                          {n.customerName ? <>Customer: {n.customerName} &bull; </> : null}
                          Inspector: {n.inspectorName || '—'} — {n.type === 'INVOICE_AGED' ? '45 days since invoice date' : 'payment received'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button type="button" disabled={!!notifActioning[n._id]}
                          onClick={() => handleMarkNotifPayable(n)}
                          style={{
                            background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: 4,
                            padding: '4px 8px', fontSize: 11, fontWeight: 600,
                            cursor: notifActioning[n._id] ? 'not-allowed' : 'pointer',
                            opacity: notifActioning[n._id] ? 0.5 : 1,
                          }}>
                          Ready to Payable
                        </button>
                        <button type="button" title="Dismiss" disabled={!!notifActioning[n._id]}
                          onClick={() => handleDismissNotif(n)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4, display: 'flex' }}>
                          <FiX size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="fin-modal-footer">
              <button className="fin-btn fin-btn-outline" onClick={() => setShowNotifModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
